import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { tokens } from '../styles/tokens.js'
import './kil-metric.js'

export interface StationChangeDetail {
  t: number
  rh: number
}

/**
 * Eine Messstation (innen oder außen): Eingabe von Temperatur und relativer Feuchte
 * plus die daraus abgeleiteten Werte. Rechnet selbst nichts, sondern meldet Änderungen
 * als `station-change` nach oben.
 */
@customElement('kil-station')
export class KilStation extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
      }

      section {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        height: 100%;
        background: var(--kil-surface);
        border: 1px solid var(--kil-line);
        border-radius: var(--kil-radius);
        padding: 1.25rem;
      }

      header {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.5rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid var(--kil-line);
      }

      h2 {
        margin: 0;
        font-size: 1rem;
        font-weight: 700;
        font-stretch: 118%;
        letter-spacing: 0.02em;
      }

      .fields {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
      }

      .field {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
      }

      label {
        font-size: 0.6875rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--kil-ink-dim);
      }

      .entry {
        display: flex;
        align-items: baseline;
        gap: 0.25rem;
        border-bottom: 2px solid var(--kil-line-strong);
        padding-bottom: 0.2rem;
        transition: border-color var(--kil-motion, 160ms) ease;
      }

      .entry:focus-within {
        border-color: var(--kil-accent);
      }

      input[type='number'] {
        width: 100%;
        min-width: 0;
        border: 0;
        padding: 0;
        background: none;
        color: inherit;
        font-family: var(--kil-font-mono);
        font-variant-numeric: tabular-nums;
        font-size: 1.75rem;
        font-weight: 500;
        letter-spacing: -0.03em;
      }

      input[type='number']:focus {
        outline: none;
      }

      input[type='number']::-webkit-outer-spin-button,
      input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      input[type='number'] {
        -moz-appearance: textfield;
        appearance: textfield;
      }

      .suffix {
        font-family: var(--kil-font-mono);
        font-size: 0.9rem;
        color: var(--kil-ink-dim);
      }

      input[type='range'] {
        width: 100%;
        accent-color: var(--kil-accent);
      }

      input[type='range']:focus-visible {
        outline: 2px solid var(--kil-accent);
        outline-offset: 4px;
        border-radius: 2px;
      }

      dl {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.75rem 1rem;
        margin: 0;
        padding-top: 0.9rem;
        border-top: 1px dashed var(--kil-line);
      }

      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }

      ::slotted(*) {
        margin: 0;
      }
    `,
  ]

  @property() heading = ''
  @property({ type: Number }) t = 20
  @property({ type: Number }) rh = 50
  @property({ type: Number }) dewPoint = 0
  @property({ type: Number }) absolute = 0
  /** Kurzer Herkunftshinweis, z. B. "Wetterdienst, vor 4 Minuten". */
  @property() source = ''

  private emit(next: Partial<StationChangeDetail>): void {
    const detail: StationChangeDetail = { t: this.t, rh: this.rh, ...next }
    this.dispatchEvent(
      new CustomEvent<StationChangeDetail>('station-change', {
        detail,
        bubbles: true,
        composed: true,
      }),
    )
  }

  private onTemperature = (event: Event): void => {
    const value = Number((event.target as HTMLInputElement).value)
    if (Number.isFinite(value)) this.emit({ t: value })
  }

  private onHumidity = (event: Event): void => {
    const value = Number((event.target as HTMLInputElement).value)
    if (Number.isFinite(value)) this.emit({ rh: Math.min(100, Math.max(0, value)) })
  }

  /**
   * Chrome verstellt Range-Inputs, sobald das Mausrad über ihnen gedreht wird.
   * Beim Scrollen durch die Seite wären damit ungewollt die Messwerte verändert.
   * Also: Wert unangetastet lassen und die Seite scrollen.
   */
  private onWheel = (event: WheelEvent): void => {
    event.preventDefault()
    window.scrollBy({ top: event.deltaY, behavior: 'instant' })
  }

  private fmt(value: number, digits = 1): string {
    return Number.isFinite(value) ? value.toFixed(digits).replace('.', ',') : '–'
  }

  override render() {
    return html`
      <section>
        <header>
          <h2>${this.heading}</h2>
          ${this.source ? html`<span class="eyebrow">${this.source}</span>` : null}
        </header>

        <div class="fields">
          <div class="field">
            <label for="t">Temperatur</label>
            <div class="entry">
              <input
                id="t"
                type="number"
                inputmode="decimal"
                step="0.1"
                min="-40"
                max="60"
                .value=${String(this.t)}
                @input=${this.onTemperature}
              />
              <span class="suffix">°C</span>
            </div>
            <input
              type="range"
              min="-20"
              max="40"
              step="0.5"
              aria-label="Temperatur grob einstellen"
              .value=${String(this.t)}
              @input=${this.onTemperature}
              @wheel=${this.onWheel}
            />
          </div>

          <div class="field">
            <label for="rh">Rel. Feuchte</label>
            <div class="entry">
              <input
                id="rh"
                type="number"
                inputmode="decimal"
                step="1"
                min="0"
                max="100"
                .value=${String(this.rh)}
                @input=${this.onHumidity}
              />
              <span class="suffix">%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              aria-label="Relative Feuchte grob einstellen"
              .value=${String(this.rh)}
              @input=${this.onHumidity}
              @wheel=${this.onWheel}
            />
          </div>
        </div>

        <dl>
          <kil-metric
            label="Absolute Feuchte"
            value=${this.fmt(this.absolute, 2)}
            unit="g/m³"
          ></kil-metric>
          <kil-metric label="Taupunkt" value=${this.fmt(this.dewPoint)} unit="°C"></kil-metric>
        </dl>

        <div class="actions"><slot></slot></div>
      </section>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kil-station': KilStation
  }
}
